/**
 * File utility functions
 */

/**
 * Validate image file type
 * @param {File} file - File to validate
 * @returns {boolean} True if valid image file
 */
export const isValidImageFile = (file) => {
  if (!file) return false;
  return file.type.match('image.*');
};

/**
 * Validate file size
 * @param {File} file - File to validate
 * @param {number} maxSizeInMB - Maximum file size in MB (default: 10)
 * @returns {boolean} True if file size is within limit
 */
export const isValidFileSize = (file, maxSizeInMB = 10) => {
  if (!file) return false;
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  return file.size <= maxSizeInBytes;
};

/**
 * Read file as data URL
 * @param {File} file - File to read
 * @returns {Promise<string>} Data URL string
 */
export const readFileAsDataURL = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Convert base64 data URL to Blob
 * @param {string} dataURL - Data URL string
 * @returns {Blob} Blob object
 * @throws {Error} If data URL format is invalid
 */
export const dataURLToBlob = (dataURL) => {
  try {
    const parts = dataURL.split(',');
    if (parts.length < 2) {
      throw new Error('Invalid data URL format');
    }
    
    const mimeMatch = parts[0].match(/:(.*?);/);
    if (!mimeMatch) {
      throw new Error('Could not extract MIME type');
    }
    
    const mime = mimeMatch[1];
    const bstr = atob(parts[1]);
    const u8arr = new Uint8Array(bstr.length);
    
    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    
    return new Blob([u8arr], { type: mime });
  } catch (error) {
    console.error('Failed to convert data URL to Blob:', error);
    throw new Error('Invalid image data format');
  }
};

/**
 * Handle file upload with validation
 * @param {File} file - File to upload
 * @param {Object} options - Validation options
 * @param {number} options.maxSizeInMB - Maximum file size in MB
 * @returns {Promise<string>} Data URL string
 * @throws {Error} If validation fails
 */
export const handleFileUpload = async (file, options = {}) => {
  const { maxSizeInMB = 10 } = options;

  if (!isValidImageFile(file)) {
    throw new Error('Please select an image file (JPG, PNG, etc.)');
  }

  if (!isValidFileSize(file, maxSizeInMB)) {
    throw new Error(`File size must be less than ${maxSizeInMB}MB`);
  }

  return readFileAsDataURL(file);
};
